import {
  Component,
  OnInit,
  OnDestroy,
  ElementRef,
  ViewChild,
  HostListener,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { TutorialService, TutorialStep } from '../services/tutorial.service';
import { Subject, takeUntil, combineLatest } from 'rxjs';

@Component({
  selector: 'app-tutorial',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div
      class="tutorial-prompt-overlay"
      [class.visible]="showPrompt"
      (click)="handlePromptOverlayClick($event)"
    >
      <div class="tutorial-prompt-modal" (click)="$event.stopPropagation()">
        <div class="prompt-header">
          <div class="prompt-icon">🤖</div>
          <h2>AI-Powered Features</h2>
          <p>
            Discover the intelligent tools that enhance your Markado experience.
          </p>
        </div>

        <div class="prompt-features">
          <div class="feature-preview">
            <div class="feature-icon">📸</div>
            <div class="feature-info">
              <h4>AI Image Search</h4>
              <p>Find similar listings using visual analysis</p>
            </div>
          </div>

          <div class="feature-preview">
            <div class="feature-icon">💬</div>
            <div class="feature-info">
              <h4>AI Assistant</h4>
              <p>Natural language search and navigation</p>
            </div>
          </div>
        </div>

        <div class="prompt-actions">
          <button class="skip-btn" (click)="skipTutorial()">Skip</button>
          <button class="start-btn" (click)="startTutorial()">
            View Features
          </button>
        </div>
      </div>
    </div>

    <div class="tutorial-overlay" [class.visible]="isActive" *ngIf="isActive">
      <div
        class="tutorial-backdrop"
        [style.mask]="getSpotlightMask()"
        [style.-webkit-mask]="getSpotlightMask()"
      ></div>

      <div
        class="tutorial-step-card"
        [class]="getStepCardClasses()"
        [style]="getStepCardPosition()"
        #stepCard
      >
        <div class="step-header">
          <div class="step-counter">
            {{ getCurrentStepNumber() }} of {{ getTotalSteps() }}
          </div>
          <button
            class="close-tutorial-btn"
            (click)="closeTutorial()"
            title="Close"
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>

        <div class="step-content">
          <h3 class="step-title">{{ currentStep?.title }}</h3>
          <p class="step-description">{{ currentStep?.description }}</p>
        </div>

        <div class="step-actions">
          <button
            class="prev-btn"
            (click)="previousStep()"
            [disabled]="isFirstStep()"
            *ngIf="!isFirstStep()"
          >
            Previous
          </button>

          <div class="step-progress">
            <div class="progress-dots">
              <div
                *ngFor="let step of steps; let i = index"
                class="progress-dot"
                [class.active]="i === currentStepIndex"
                [class.completed]="i < currentStepIndex"
              ></div>
            </div>
          </div>

          <button class="next-btn" (click)="nextStep()">
            {{ getActionButtonText() }}
          </button>
        </div>
      </div>
    </div>
  `,
  styles: [
    `
      .tutorial-prompt-overlay {
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: rgba(0, 0, 0, 0.7);
        backdrop-filter: blur(4px);
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 9999;
        opacity: 0;
        visibility: hidden;
        transition: all 0.3s ease;
        padding: 20px;
        box-sizing: border-box;
      }

      .tutorial-prompt-overlay.visible {
        opacity: 1;
        visibility: visible;
      }

      .tutorial-prompt-modal {
        background: white;
        border-radius: 20px;
        padding: 32px;
        max-width: 480px;
        width: 100%;
        text-align: center;
        box-shadow: 0 20px 50px rgba(0, 0, 0, 0.15);
        transform: scale(0.95);
        transition: transform 0.3s ease;
        animation: modalSlideUp 0.4s ease-out;
      }

      .tutorial-prompt-overlay.visible .tutorial-prompt-modal {
        transform: scale(1);
      }

      @keyframes modalSlideUp {
        from {
          opacity: 0;
          transform: translateY(20px) scale(0.95);
        }
        to {
          opacity: 1;
          transform: translateY(0) scale(1);
        }
      }

      .prompt-header {
        margin-bottom: 24px;
      }

      .prompt-icon {
        font-size: 48px;
        margin-bottom: 16px;
      }

      .prompt-header h2 {
        margin: 0 0 12px 0;
        font-size: 24px;
        font-weight: 600;
        color: #1f2937;
      }

      .prompt-header p {
        margin: 0;
        color: #6b7280;
        font-size: 16px;
        line-height: 1.5;
      }

      .prompt-features {
        display: flex;
        gap: 16px;
        margin-bottom: 32px;
        flex-direction: column;
      }

      .feature-preview {
        display: flex;
        align-items: center;
        gap: 16px;
        padding: 16px;
        background: #f8fafc;
        border-radius: 12px;
        text-align: left;
      }

      .feature-icon {
        font-size: 28px;
        width: 48px;
        height: 48px;
        display: flex;
        align-items: center;
        justify-content: center;
        background: white;
        border-radius: 50%;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
      }

      .feature-info h4 {
        margin: 0 0 4px 0;
        font-size: 16px;
        font-weight: 600;
        color: #1f2937;
      }

      .feature-info p {
        margin: 0;
        font-size: 14px;
        color: #6b7280;
      }

      .prompt-actions {
        display: flex;
        gap: 16px;
        justify-content: center;
      }

      .skip-btn,
      .start-btn {
        padding: 12px 24px;
        border-radius: 10px;
        font-size: 15px;
        font-weight: 600;
        cursor: pointer;
        transition: all 0.2s ease;
        border: none;
        min-width: 120px;
      }

      .skip-btn {
        background: #f3f4f6;
        color: #4b5563;
      }

      .skip-btn:hover {
        background: #e5e7eb;
        transform: translateY(-1px);
      }

      .start-btn {
        background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
        color: white;
        box-shadow: 0 4px 15px rgba(59, 130, 246, 0.3);
      }

      .start-btn:hover {
        box-shadow: 0 8px 25px rgba(59, 130, 246, 0.4);
        transform: translateY(-2px);
      }

      .tutorial-overlay {
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        z-index: 9998;
        opacity: 0;
        visibility: hidden;
        transition: all 0.3s ease;
      }

      .tutorial-overlay.visible {
        opacity: 1;
        visibility: visible;
      }

      .tutorial-backdrop {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: rgba(0, 0, 0, 0.8);
        transition: mask 0.4s ease-in-out, -webkit-mask 0.4s ease-in-out;
      }

      .tutorial-step-card {
        position: absolute;
        background: white;
        border-radius: 16px;
        padding: 24px;
        max-width: 320px;
        min-width: 280px;
        box-shadow: 0 20px 50px rgba(0, 0, 0, 0.2);
        z-index: 1;
        transition: top 0.4s ease-in-out, left 0.4s ease-in-out,
          transform 0.4s ease-in-out;
        animation: stepCardSlideIn 0.4s ease-out;
      }

      .tutorial-step-card.ai-assistant-step {
        transform: translate(-100px, -50px);
      }

      @keyframes stepCardSlideIn {
        from {
          opacity: 0;
          transform: scale(0.9);
        }
        to {
          opacity: 1;
          transform: scale(1);
        }
      }

      .tutorial-step-card.welcome-step,
      .tutorial-step-card.complete-step {
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        text-align: center;
        max-width: 400px;
      }

      .step-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 16px;
      }

      .step-counter {
        font-size: 12px;
        color: #6b7280;
        font-weight: 500;
        background: #f3f4f6;
        padding: 4px 8px;
        border-radius: 6px;
      }

      .close-tutorial-btn {
        background: none;
        border: none;
        color: #9ca3af;
        cursor: pointer;
        padding: 4px;
        border-radius: 4px;
        transition: all 0.2s ease;
        width: 24px;
        height: 24px;
        display: flex;
        align-items: center;
        justify-content: center;
      }

      .close-tutorial-btn:hover {
        color: #6b7280;
        background: #f3f4f6;
      }

      .close-tutorial-btn svg {
        width: 16px;
        height: 16px;
      }

      .step-content {
        margin-bottom: 24px;
      }

      .step-title {
        margin: 0 0 8px 0;
        font-size: 18px;
        font-weight: 600;
        color: #1f2937;
      }

      .step-description {
        margin: 0;
        color: #4b5563;
        line-height: 1.5;
        font-size: 14px;
      }

      .step-actions {
        display: flex;
        justify-content: space-between;
        align-items: center;
        gap: 12px;
      }

      .prev-btn,
      .next-btn {
        padding: 8px 16px;
        border-radius: 8px;
        font-size: 14px;
        font-weight: 500;
        cursor: pointer;
        transition: all 0.2s ease;
        border: none;
        min-width: 80px;
      }

      .prev-btn {
        background: #f3f4f6;
        color: #4b5563;
      }

      .prev-btn:hover:not(:disabled) {
        background: #e5e7eb;
      }

      .prev-btn:disabled {
        opacity: 0.5;
        cursor: not-allowed;
      }

      .next-btn {
        background: #3b82f6;
        color: white;
      }

      .next-btn:hover {
        background: #2563eb;
        transform: translateY(-1px);
      }

      .step-progress {
        flex: 1;
        display: flex;
        justify-content: center;
      }

      .progress-dots {
        display: flex;
        gap: 6px;
        align-items: center;
      }

      .progress-dot {
        width: 8px;
        height: 8px;
        border-radius: 50%;
        background: #e5e7eb;
        transition: all 0.3s ease;
      }

      .progress-dot.active {
        background: #3b82f6;
        transform: scale(1.2);
      }

      .progress-dot.completed {
        background: #10b981;
      }

      @media (max-width: 768px) {
        .tutorial-prompt-overlay {
          padding: 16px;
        }

        .tutorial-prompt-modal {
          padding: 24px 20px;
          max-width: 95vw;
          margin: 0;
          border-radius: 16px;
        }

        .prompt-header {
          margin-bottom: 20px;
        }

        .prompt-icon {
          font-size: 40px;
          margin-bottom: 12px;
        }

        .prompt-header h2 {
          font-size: 22px;
          margin-bottom: 8px;
        }

        .prompt-header p {
          font-size: 15px;
        }

        .prompt-features {
          gap: 12px;
          margin-bottom: 24px;
        }

        .feature-preview {
          padding: 14px;
          gap: 12px;
        }

        .feature-icon {
          font-size: 24px;
          width: 44px;
          height: 44px;
          flex-shrink: 0;
        }

        .feature-info h4 {
          font-size: 15px;
        }

        .feature-info p {
          font-size: 13px;
          line-height: 1.4;
        }

        .prompt-actions {
          gap: 12px;
          flex-direction: column;
        }

        .skip-btn,
        .start-btn {
          width: 100%;
          min-width: auto;
          padding: 14px 20px;
          font-size: 16px;
        }

        .tutorial-step-card {
          padding: 20px 18px;
          max-width: calc(100vw - 24px);
          min-width: auto;
          margin: 0 12px;
          border-radius: 12px;
          transform: none !important;
        }

        .tutorial-step-card.ai-assistant-step {
          top: auto !important;
          left: 12px !important;
          right: 12px !important;
          bottom: 120px !important;
          transform: none !important;
          max-width: none;
          width: auto;
        }

        .tutorial-step-card.welcome-step,
        .tutorial-step-card.complete-step {
          max-width: calc(100vw - 24px);
          margin: 0 12px;
          padding: 24px 20px;
          top: 50% !important;
          left: 50% !important;
          transform: translate(-50%, -50%) !important;
        }

        .step-header {
          margin-bottom: 14px;
        }

        .step-counter {
          font-size: 11px;
          padding: 3px 7px;
        }

        .close-tutorial-btn {
          width: 28px;
          height: 28px;
          padding: 6px;
        }

        .close-tutorial-btn svg {
          width: 14px;
          height: 14px;
        }

        .step-content {
          margin-bottom: 18px;
        }

        .step-title {
          font-size: 17px;
          margin-bottom: 6px;
        }

        .step-description {
          font-size: 14px;
          line-height: 1.4;
        }

        .step-actions {
          gap: 10px;
          flex-direction: column;
          align-items: stretch;
        }

        .step-progress {
          order: -1;
          margin-bottom: 12px;
          justify-content: center;
        }

        .progress-dots {
          gap: 8px;
        }

        .progress-dot {
          width: 10px;
          height: 10px;
        }

        .step-actions .prev-btn,
        .step-actions .next-btn {
          min-width: auto;
          font-size: 15px;
          padding: 12px 16px;
          border-radius: 10px;
          flex: 1;
        }

        .step-actions .prev-btn {
          order: 1;
        }

        .step-actions .next-btn {
          order: 2;
        }

        .step-actions::after {
          content: '';
          display: block;
          width: 100%;
          order: 3;
        }

        .step-actions .prev-btn,
        .step-actions .next-btn {
          width: 100%;
          margin-bottom: 8px;
        }

        .step-actions .next-btn {
          margin-bottom: 0;
        }
      }

      @media (max-width: 480px) {
        .tutorial-prompt-overlay {
          padding: 12px;
        }

        .tutorial-prompt-modal {
          padding: 20px 16px;
        }

        .tutorial-step-card {
          margin: 0 8px;
          padding: 16px 14px;
        }

        .tutorial-step-card.ai-assistant-step {
          left: 8px !important;
          right: 8px !important;
          bottom: 100px !important;
        }

        .tutorial-step-card.welcome-step,
        .tutorial-step-card.complete-step {
          margin: 0 8px;
          padding: 20px 16px;
        }

        .step-title {
          font-size: 16px;
        }

        .step-description {
          font-size: 13px;
        }
      }

      :global(body.tutorial-mode .reverse-search-button) {
        position: relative;
        z-index: 9999;
        box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.3) !important;
        border-radius: 50% !important;
      }

      :global(body.tutorial-mode .floating-chat-button) {
        position: relative;
        z-index: 9999;
        box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.3) !important;
      }
    `,
  ],
})
export class TutorialComponent implements OnInit, OnDestroy {
  @ViewChild('stepCard') stepCard!: ElementRef;

  private destroy$ = new Subject<void>();

  showPrompt = false;
  isActive = false;
  currentStep: TutorialStep | null = null;
  currentStepIndex = -1;
  steps: TutorialStep[] = [];

  private targetElement: Element | null = null;
  private targetRect: DOMRect | null = null;

  constructor(private tutorialService: TutorialService) {}

  ngOnInit(): void {
    combineLatest([
      this.tutorialService.isActive$,
      this.tutorialService.currentStep$,
      this.tutorialService.steps$,
    ])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([isActive, currentStepIndex, steps]) => {
        this.isActive = isActive;
        this.currentStepIndex = currentStepIndex;
        this.steps = steps;
        this.currentStep = this.tutorialService.getCurrentStep();

        if (isActive && this.currentStep) {
          requestAnimationFrame(() => {
            this.updateTargetElement();
          });
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  showTutorialPrompt(): void {
    this.showPrompt = true;
  }

  startTutorial(): void {
    this.showPrompt = false;
    this.tutorialService.startTutorial();
  }

  skipTutorial(): void {
    this.showPrompt = false;
    this.tutorialService.skipTutorial();
  }

  nextStep(): void {
    this.tutorialService.nextStep();
  }

  previousStep(): void {
    this.tutorialService.previousStep();
  }

  closeTutorial(): void {
    this.tutorialService.skipTutorial();
  }

  isFirstStep(): boolean {
    return this.currentStepIndex <= 0;
  }

  isLastStep(): boolean {
    return this.currentStepIndex >= this.steps.length - 1;
  }

  isWelcomeStep(): boolean {
    return this.currentStep?.id === 'welcome';
  }

  isCompleteStep(): boolean {
    return this.currentStep?.id === 'complete';
  }

  getCurrentStepNumber(): number {
    return this.tutorialService.getCurrentStepNumber();
  }

  getTotalSteps(): number {
    return this.tutorialService.getTotalSteps();
  }

  getActionButtonText(): string {
    if (this.isLastStep()) {
      return 'Done';
    }
    return 'Next';
  }

  getStepCardClasses(): string {
    const classes = [];

    if (this.isWelcomeStep() || this.isCompleteStep()) {
      classes.push(this.isWelcomeStep() ? 'welcome-step' : 'complete-step');
    }

    if (this.currentStep?.title === 'AI Assistant') {
      classes.push('ai-assistant-step');
    }

    return classes.join(' ');
  }

  getStepCardPosition(): any {
    const style: { [key: string]: string } = {
      position: 'fixed',
    };

    if (this.isWelcomeStep() || this.isCompleteStep()) {
      style['top'] = '50%';
      style['left'] = '50%';
      style['transform'] = 'translate(-50%, -50%)';
      return style;
    }

    if (!this.targetRect) {
      style['top'] = '50%';
      style['left'] = '50%';
      style['transform'] = 'translate(-50%, -50%)';
      return style;
    }

    const padding = 20;
    const cardWidth = 320;
    const cardHeight = 200;

    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    let top = 0;
    let left = 0;

    switch (this.currentStep?.position) {
      case 'top':
        top = this.targetRect.top - cardHeight - padding;
        left = this.targetRect.left + this.targetRect.width / 2 - cardWidth / 2;
        break;

      case 'bottom':
        top = this.targetRect.bottom + padding;
        left = this.targetRect.left + this.targetRect.width / 2 - cardWidth / 2;
        break;

      case 'left':
        top = this.targetRect.top + this.targetRect.height / 2 - cardHeight / 2;
        left = this.targetRect.left - cardWidth - padding;
        break;

      case 'right':
        top = this.targetRect.top + this.targetRect.height / 2 - cardHeight / 2;
        left = this.targetRect.right + padding;
        break;
    }

    left = Math.max(
      padding,
      Math.min(left, viewportWidth - cardWidth - padding)
    );
    top = Math.max(
      padding,
      Math.min(top, viewportHeight - cardHeight - padding)
    );

    style['top'] = `${top}px`;
    style['left'] = `${left}px`;

    return style;
  }

  getSpotlightMask(): string {
    if (!this.targetRect || this.isWelcomeStep() || this.isCompleteStep()) {
      return 'none';
    }

    const padding = 15;

    const centerX = this.targetRect.left + this.targetRect.width / 2;
    const centerY = this.targetRect.top + this.targetRect.height / 2;
    const elementRadius =
      Math.max(this.targetRect.width, this.targetRect.height) / 2;
    const spotlightRadius = elementRadius + padding;

    return `radial-gradient(circle ${spotlightRadius}px at ${centerX}px ${centerY}px, transparent ${spotlightRadius}px, black ${
      spotlightRadius + 1
    }px)`;
  }

  private updateTargetElement(): void {
    if (!this.currentStep?.targetSelector) {
      this.targetElement = null;
      this.targetRect = null;
      return;
    }

    this.targetElement = document.querySelector(
      this.currentStep!.targetSelector
    );
    if (this.targetElement) {
      this.targetRect = this.targetElement.getBoundingClientRect();
    } else {
      this.targetRect = null;
    }
  }

  handlePromptOverlayClick(event: MouseEvent): void {
    if (event.target === event.currentTarget) {
      this.skipTutorial();
    }
  }

  @HostListener('window:resize', ['$event'])
  onResize(): void {
    if (this.isActive && this.currentStep?.targetSelector) {
      requestAnimationFrame(() => {
        this.updateTargetElement();
      });
    }
  }
}
